import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { ShieldCheck, CheckCircle2, AlertTriangle, XCircle, FileText, Hash } from 'lucide-react';
import { Modal } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Spinner } from '@/shared/components/ui/Spinner';
import { useSampleMutations } from '../api';
import type { SampleRequest, TechnicalDecision } from '../types';

interface SampleTechnicalApprovalModalProps {
  open: boolean;
  onClose: () => void;
  sample: SampleRequest | null;
}

const DECISIONS: Array<{
  id: TechnicalDecision;
  title: string;
  desc: string;
  icon: typeof CheckCircle2;
  color: string;
  bg: string;
  border: string;
}> = [
  {
    id: 'approved',
    title: 'Technical Approval (Unconditional)',
    desc: 'Chemical properties and lab performance tests passed. Cleared for bulk quotation.',
    icon: CheckCircle2,
    color: 'text-emerald-700',
    bg: 'bg-emerald-50/70',
    border: 'border-emerald-200',
  },
  {
    id: 'conditional',
    title: 'Conditional Approval',
    desc: 'Approved with batch adjustment notes (e.g. blend additive package at +2% in bulk run).',
    icon: AlertTriangle,
    color: 'text-amber-700',
    bg: 'bg-amber-50/70',
    border: 'border-amber-200',
  },
  {
    id: 'rejected',
    title: 'Technical Rejection',
    desc: 'Failed internal lab or viscosity standards. Reverts to feedback for re-formulation.',
    icon: XCircle,
    color: 'text-red-700',
    bg: 'bg-red-50/70',
    border: 'border-red-200',
  },
];

export function SampleTechnicalApprovalModal({
  open,
  onClose,
  sample,
}: SampleTechnicalApprovalModalProps) {
  const { approveTechnical, rejectTechnical } = useSampleMutations();

  const [decision, setDecision] = useState<TechnicalDecision>('approved');
  const [testReportRef, setTestReportRef] = useState('');
  const [notes, setNotes] = useState('');

  if (!sample) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (decision === 'rejected') {
      if (!notes.trim()) {
        toast.error('Please specify the technical reason for rejecting this sample.');
        return;
      }
      rejectTechnical.mutate(
        { id: sample._id, notes: notes.trim() },
        {
          onSuccess: () => {
            toast.success(`Technical rejection recorded for ${sample.code}. Returned to feedback review.`);
            onClose();
          },
          onError: (err: any) => {
            toast.error(err?.response?.data?.error || 'Failed to record technical rejection.');
          },
        },
      );
    } else {
      approveTechnical.mutate(
        {
          id: sample._id,
          payload: {
            decision,
            testReportRef: testReportRef.trim(),
            notes: notes.trim(),
          },
        },
        {
          onSuccess: () => {
            toast.success(`Technical sign-off recorded for ${sample.code}.`);
            onClose();
          },
          onError: (err: any) => {
            toast.error(err?.response?.data?.error || 'Failed to record technical sign-off.');
          },
        },
      );
    }
  };

  const isSubmitting = approveTechnical.isPending || rejectTechnical.isPending;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Technical Review & Lab Sign-Off: ${sample.code}`}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="p-3 bg-purple-50/60 border border-purple-100 rounded-xl flex items-start gap-2.5">
          <ShieldCheck className="w-4 h-4 text-purple-600 mt-0.5" />
          <div className="text-xs text-purple-900">
            <span className="font-bold">Customer:</span> {sample.customerName} ·{' '}
            {sample.lines?.map((l) => `${l.productName} (${l.packSize})`).join(', ')}
            <p className="text-purple-700 text-data-xs mt-0.5">
              Customer Feedback: <span className="font-semibold">{sample.feedback?.outcome}</span>
              {sample.feedback?.notes ? ` — "${sample.feedback.notes}"` : ''}
            </p>
          </div>
        </div>

        {/* Decision Cards */}
        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-2">
            Technical Evaluation Decision <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-1 gap-2.5">
            {DECISIONS.map((item) => {
              const Icon = item.icon;
              const isSelected = decision === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setDecision(item.id)}
                  className={`p-3 rounded-xl border text-left transition-all flex items-start gap-3 ${
                    isSelected
                      ? `${item.border} ${item.bg} ring-2 ring-purple-500`
                      : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}
                >
                  <Icon className={`w-4 h-4 mt-0.5 ${item.color}`} />
                  <div>
                    <div className="text-xs font-bold text-slate-900">{item.title}</div>
                    <div className="text-data-xs text-slate-500 mt-0.5 leading-tight">{item.desc}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Test Report Reference */}
        {decision !== 'rejected' && (
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1 flex items-center gap-1">
              <Hash className="w-3.5 h-3.5 text-slate-500" /> Lab Test Report / QA Certificate Ref No.
            </label>
            <Input
              value={testReportRef}
              onChange={(e) => setTestReportRef(e.target.value)}
              placeholder="e.g. LAB-2026-TR-0042"
              className="font-mono text-xs"
            />
          </div>
        )}

        {/* Notes */}
        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-1 flex items-center gap-1">
            <FileText className="w-3.5 h-3.5 text-slate-500" />
            Technical / Formulation Remarks{' '}
            {decision === 'rejected' && <span className="text-red-500">* (Mandatory on rejection)</span>}
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={
              decision === 'rejected'
                ? 'Specify technical non-conformance (e.g. Viscosity index out of spec, Flash point < 210°C)...'
                : 'Formulation comments, quality engineer observations, or bulk production instructions...'
            }
            rows={3}
            className="w-full p-2.5 rounded-lg border border-slate-300 text-xs focus:ring-2 focus:ring-purple-500 focus:outline-none resize-none"
            required={decision === 'rejected'}
          />
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-200">
          <Button type="button" variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting} className="gap-2 bg-purple-600 hover:bg-purple-700 text-white">
            {isSubmitting ? (
              <>
                <Spinner className="h-4 w-4" /> Recording...
              </>
            ) : (
              <>
                <ShieldCheck className="w-4 h-4" />
                Confirm Sign-Off
              </>
            )}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
